<!-- 网站自定义页面配置 type=0 -->
<template>
  <div class="template-editor-page">
    <!-- 左侧：模版列表 + 代码编辑 -->
    <div class="editor-panel">
      <div class="panel-header">
        <span class="panel-title">网站自定义页面配置</span>
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAdd">新建模版</el-button>
      </div>

      <!-- 模版选择 -->
      <div class="template-select-row">
        <span class="select-label">选择模版</span>
        <el-select
          v-model="currentId"
          placeholder="请选择模版"
          size="small"
          style="flex:1"
          @change="handleSelect"
        >
          <el-option
            v-for="item in templateList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
        <el-button
          v-if="currentId"
          size="mini"
          type="danger"
          icon="el-icon-delete"
          style="margin-left:8px"
          :disabled="isDeleteDisabled"
          @click="handleDelete"
        >删除</el-button>
      </div>

      <!-- 名称输入 -->
      <div class="name-row">
        <span class="select-label">模版名称</span>
        <el-input
          v-model="form.name"
          placeholder="请输入模版名称"
          size="small"
          style="flex:1"
          prefix-icon="el-icon-document"
        ></el-input>
      </div>

      <!-- 代码编辑区 -->
      <textarea
        class="code-editor"
        v-model="form.content"
        placeholder="请输入 HTML/CSS 代码..."
        spellcheck="false"
      ></textarea>

      <!-- 操作按钮 -->
      <div class="editor-actions">
        <el-button type="success" size="small" icon="el-icon-check" :loading="saving" @click="handleSave">保存</el-button>
        <el-button type="primary" size="small" icon="el-icon-view" @click="refreshPreview">刷新预览</el-button>
      </div>
    </div>

    <!-- 右侧：预览 -->
    <div class="preview-panel">
      <div class="preview-header">
        <span>预览</span>
        <el-tag size="mini" type="info">原生 HTML + CSS 渲染</el-tag>
      </div>
      <iframe
        class="preview-iframe"
        ref="previewIframe"
        sandbox="allow-same-origin allow-scripts"
        frameborder="0"
      ></iframe>
    </div>
  </div>
</template>

<script>
import {
  selectTemplateCodeApi,
  addTemplateCodeApi,
  updateTemplateCodeApi,
  deleteTemplateCodeApi,
} from "@/services/templateCode/index.js";

const TEMPLATE_TYPE = 0;

export default {
  name: "CustomPage",
  computed: {
    currentTemplate() {
      return this.templateList.find((item) => item.id === this.currentId) || null;
    },
    isDeleteDisabled() {
      return Number(this.currentTemplate?.disabled_del) === 1;
    },
  },
  data() {
    return {
      templateList: [],
      currentId: null,
      form: {
        name: "",
        content: "",
      },
      saving: false,
    };
  },
  mounted() {
    this.loadList();
  },
  methods: {
    async loadList() {
      const res = await selectTemplateCodeApi({ type: TEMPLATE_TYPE, pageNumber: 1, pageSize: 100 });
      if (res?.code === 200) {
        this.templateList = res.data || [];
        // 若有列表，默认选中第一条
        if (this.templateList.length > 0 && !this.currentId) {
          this.currentId = this.templateList[0].id;
          this.handleSelect(this.currentId);
        }
      }
    },

    handleSelect(id) {
      const item = this.templateList.find((t) => t.id === id);
      if (item) {
        this.form.name = item.name;
        this.form.content = item.content;
        this.$nextTick(() => this.refreshPreview());
      }
    },

    handleAdd() {
      this.currentId = null;
      this.form.name = "";
      this.form.content = "";
      this.updateIframe("");
    },

    async handleSave() {
      if (!this.form.name) {
        this.$message.warning("请输入模版名称");
        return;
      }
      this.saving = true;
      try {
        let res;
        if (this.currentId) {
          res = await updateTemplateCodeApi({
            id: this.currentId,
            name: this.form.name,
            content: this.form.content,
            type: TEMPLATE_TYPE,
          });
        } else {
          res = await addTemplateCodeApi({
            name: this.form.name,
            content: this.form.content,
            type: TEMPLATE_TYPE,
          });
        }
        if (res?.code === 200) {
          this.$message.success("保存成功");
          await this.loadList();
          // 新增后自动选中新建项
          if (!this.currentId && this.templateList.length > 0) {
            this.currentId = this.templateList[0].id;
          }
          this.refreshPreview();
        } else {
          this.$message.error(res?.message || "保存失败");
        }
      } finally {
        this.saving = false;
      }
    },

    async handleDelete() {
      if (this.isDeleteDisabled) {
        this.$message.warning("该模版禁止删除");
        return;
      }
      await this.$confirm("确定删除该模版？", "提示", { type: "warning" });
      const res = await deleteTemplateCodeApi({ id: this.currentId });
      if (res?.code === 200) {
        this.$message.success("删除成功");
        this.currentId = null;
        this.form.name = "";
        this.form.content = "";
        this.updateIframe("");
        await this.loadList();
      } else {
        this.$message.error(res?.message || "删除失败");
      }
    },

    refreshPreview() {
      this.updateIframe(this.form.content);
    },

    updateIframe(html) {
      const iframe = this.$refs.previewIframe;
      if (!iframe) return;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html || "<div style='color:#999;padding:20px'>暂无内容</div>");
      doc.close();
    },
  },
};
</script>

<style scoped>
.template-editor-page {
  display: flex;
  height: calc(100vh - 100px);
  gap: 0;
  background: #f5f7fa;
}

/* 左侧 */
.editor-panel {
  width: 50%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  padding: 16px;
  box-sizing: border-box;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.template-select-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.select-label {
  flex-shrink: 0;
  font-size: 13px;
  color: #606266;
  margin-right: 8px;
  white-space: nowrap;
}

.name-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.code-editor {
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 10px;
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #303133;
  background: #1e1e1e;
  color: #d4d4d4;
  resize: none;
  outline: none;
  tab-size: 2;
  white-space: pre;
  overflow: auto;
}

.editor-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

/* 右侧 */
.preview-panel {
  width: 50%;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-sizing: border-box;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  background: #fafafa;
}

.preview-iframe {
  flex: 1;
  width: 100%;
  border: none;
  background: #fff;
}
</style>
